/**
 * Pluckonomics — PooBucks (account credits) comp panel (Phase E.2).
 *
 * Reads `GET /api/admin/analytics/poobucks-summary`.
 * Surfaces three cents-of-comp totals (issued / redeemed / outstanding),
 * an MTD activity strip, and a recent-grants ledger feed (top 20).
 *
 * Empty-state: when the backend reports
 * `display_state="stripe_gated"` (no ledger entries at all), every
 * numeric tile renders "—" with the orange pill. The recent-grants
 * panel renders a soft "No grants yet" line.
 */
import { Card, CardContent } from "@/components/ui/card";

function _formatCents(cents) {
  if (cents === null || cents === undefined) return "—";
  // Signed-aware: cents can be negative for redemption rows.
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return sign + new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(abs / 100);
}

function _formatRelativeDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

function StatTile({ label, cents, gated, testIdSuffix, tone = "neutral" }) {
  const toneColor =
    tone === "positive" ? "var(--pp-text)" :
    tone === "negative" ? "var(--pp-orange)" :
    "var(--pp-text)";
  return (
    <div
      className="rounded-md p-3"
      style={{ background: "var(--pp-bg)", border: "1px solid var(--pp-border)" }}
      data-testid={`pluckonomics-poobucks-${testIdSuffix}`}
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.06em]"
           style={{ color: "var(--pp-text-soft)" }}>
        {label}
      </div>
      <div className="mt-1 text-xl font-semibold tracking-tight"
           style={{ color: gated ? "var(--pp-text-soft)" : toneColor }}>
        {gated ? "—" : _formatCents(cents)}
      </div>
    </div>
  );
}

export default function PooBucksPanel({ data }) {
  if (!data) {
    return (
      <div className="p-6 text-sm" style={{ color: "var(--pp-text-soft)" }}
           data-testid="pluckonomics-poobucks-loading">
        Loading PooBucks…
      </div>
    );
  }
  const gated = data.display_state === "stripe_gated";
  return (
    <Card
      style={{ background: "var(--pp-v3-surface)", borderColor: "var(--pp-border)" }}
      data-testid="pluckonomics-poobucks-card"
    >
      <CardContent className="p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold" style={{ color: "var(--pp-text)" }}
              data-testid="pluckonomics-poobucks-heading">
            PooBucks comps
          </h2>
          <p className="mt-1 text-xs" style={{ color: "var(--pp-text-soft)" }}>
            Account credits issued, redeemed, and outstanding across all
            customer + partner balances.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3"
             data-testid="pluckonomics-poobucks-totals">
          <StatTile label="Issued"      cents={data.issued_cents}
                    gated={gated} testIdSuffix="issued" tone="positive" />
          <StatTile label="Redeemed"    cents={data.redeemed_cents}
                    gated={gated} testIdSuffix="redeemed" tone="negative" />
          <StatTile label="Outstanding" cents={data.outstanding_cents}
                    gated={gated} testIdSuffix="outstanding" tone="positive" />
        </div>

        <div className="mt-4 flex items-center justify-between text-xs"
             data-testid="pluckonomics-poobucks-mtd-strip"
             style={{ color: "var(--pp-text-soft)" }}>
          <span>
            MTD issued: <span style={{ color: "var(--pp-text)", fontWeight: 600 }}
                              data-testid="pluckonomics-poobucks-mtd-issued">
              {gated ? "—" : _formatCents(data.mtd_issued_cents)}
            </span>
          </span>
          <span>
            MTD redeemed: <span style={{ color: "var(--pp-text)", fontWeight: 600 }}
                                data-testid="pluckonomics-poobucks-mtd-redeemed">
              {gated ? "—" : _formatCents(data.mtd_redeemed_cents)}
            </span>
          </span>
          <span>
            Active (30d): <span style={{ color: "var(--pp-text)", fontWeight: 600 }}
                                data-testid="pluckonomics-poobucks-active-30d">
              {gated ? "—" : data.active_users_30d?.toLocaleString("en-US")}
            </span>
          </span>
        </div>

        <div className="mt-5">
          <div className="text-[11px] font-bold uppercase tracking-[0.06em] mb-2"
               style={{ color: "var(--pp-text-soft)" }}>
            Recent grants
          </div>
          {(!data.recent_grants || data.recent_grants.length === 0) ? (
            <div className="text-sm py-4"
                 style={{ color: "var(--pp-text-soft)" }}
                 data-testid="pluckonomics-poobucks-empty">
              No grants yet — admin grants from the Credits tab will appear here.
            </div>
          ) : (
            <ul className="space-y-1 max-h-[260px] overflow-y-auto"
                data-testid="pluckonomics-poobucks-recent-list">
              {data.recent_grants.map((g) => (
                <li key={g.id} className="flex items-start justify-between py-1.5"
                    style={{ borderBottom: "1px solid var(--pp-border)" }}
                    data-testid={`pluckonomics-poobucks-grant-${g.id}`}>
                  <div className="min-w-0 flex-1 pr-3">
                    <div className="text-sm truncate"
                         style={{ color: "var(--pp-text)" }}
                         title={g.user_email}>
                      {g.user_email}
                    </div>
                    <div className="text-[11px] truncate"
                         style={{ color: "var(--pp-text-soft)" }}
                         title={g.reason}>
                      {g.reason}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold tabular-nums"
                         style={{
                           color: g.amount_cents >= 0
                             ? "var(--pp-text)"
                             : "var(--pp-orange)",
                         }}>
                      {g.amount_cents >= 0 ? "+" : ""}{_formatCents(g.amount_cents)}
                    </div>
                    <div className="text-[11px]"
                         style={{ color: "var(--pp-text-soft)" }}>
                      {_formatRelativeDate(g.created_at)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
